import {normalizeSplineInterpolationPoints} from './normalizeSplineInterpolationPoints';
import {sampleClosedSplineInterpolationPoints} from './sampleClosedSplineInterpolationPoints';
import {sampleOpenSplineInterpolationPoints} from './sampleOpenSplineInterpolationPoints';
import {
    sampleRationalBezierPoints,
    sampleRationalBSplinePoints,
    type CurveSamplePoint3,
} from './curveSamplingMath';

/**
 * CPU 同步曲线采样所需的元数据字段。
 *
 * 设计说明：
 * 该结构刻意使用字符串字段而非引入草图对象的枚举类型，避免 tool 层反向依赖 mesh 层形成循环依赖；
 * 取值约定与 `SketchBezierCurveKind` / `SketchBezierCurveClosureKind` 一致：
 * curveKind = 'spline' | 'bezier' | 'bspline'，closureKind = 'open' | 'closed'。
 */
export interface SynchronousCurveSamplingMetadataLike {
    /** 曲线重建类型。 */
    curveKind: string;
    /** 曲线闭合状态。 */
    closureKind: string;
    /** 当前曲线阶数；仅 B 样条或可调贝塞尔时使用。 */
    degree: number | null;
    /** 控制点权重列表。 */
    weights: number[];
    /** 当前是否为理性曲线。 */
    isRational: boolean;
}

/** 闭合曲线采样输入标记。 */
const CLOSED_CLOSURE_KIND = 'closed';

/**
 * 自闭合曲线在采样前把首点补到末尾。
 * 说明：
 * 1. 与 Worker 重建路径的闭合输入处理保持一致（见样条对象的 `resolveCurveInputControlPointPositions`）；
 * 2. 插值样条（spline）的闭合采样由 `sampleClosedSplineInterpolationPoints` 内部自行去重，
 *    不需要在这里补点；该处理只面向 Bezier / BSpline 控制点语义。
 * @param controlPointPositions 当前控制点位置列表。
 * @param closureKind 曲线闭合状态。
 * @returns 用于采样的控制点位置列表。
 */
function resolveCurveSamplingInputPositions(
    controlPointPositions: readonly CurveSamplePoint3[],
    closureKind: string,
): CurveSamplePoint3[] {
    if (
        closureKind !== CLOSED_CLOSURE_KIND
        || controlPointPositions.length < 2
    ) {
        return controlPointPositions.map((point) => ({...point}));
    }
    return [
        ...controlPointPositions.map((point) => ({...point})),
        {...controlPointPositions[0]},
    ];
}

/**
 * 按曲线元数据在 CPU 端同步采样曲线显示点。
 *
 * 设计说明：
 * 1. 该函数是 Worker 曲线求解路径（`compute2DCurve`）的同步轻量镜像，
 *    输入处理（插值点规范化、闭合补点）与 Worker 完全一致；
 * 2. 插值样条（spline）的开放曲线使用 Catmull-Rom 轻量近似，
 *    闭合曲线直接复用与 Worker 相同的周期三次 B 样条插值；
 * 3. 服务场景是拖拽期逐帧实时预览：只要视觉跟手，最终几何仍由松手后的
 *    Worker 精确重建收口，因此开放插值允许近似；
 * 4. Bezier / BSpline 直接复用与 Worker 完全相同的纯数学采样算法，结果一致。
 *
 * @param controlPointPositions 控制点位置列表。
 * @param curveMetadata 曲线元数据字段。
 * @param segments 采样段数。
 * @returns 曲线采样点列表。
 */
export function sampleCurvePointsSynchronously(
    controlPointPositions: readonly CurveSamplePoint3[],
    curveMetadata: SynchronousCurveSamplingMetadataLike,
    segments: number,
): CurveSamplePoint3[] {
    if (controlPointPositions.length < 2) {
        return controlPointPositions.map((point) => ({...point}));
    }

    if (curveMetadata.curveKind === 'spline') {
        // 插值样条：先压缩连续重复点，再按闭合状态选择采样器。
        const normalizedPoints = normalizeSplineInterpolationPoints(controlPointPositions);
        if (curveMetadata.closureKind === CLOSED_CLOSURE_KIND) {
            return sampleClosedSplineInterpolationPoints(normalizedPoints, segments);
        }
        return sampleOpenSplineInterpolationPoints(normalizedPoints, segments);
    }

    // Bezier / BSpline 属于控制点语义曲线，闭合时补首点后按控制点采样。
    const curveInputPositions = resolveCurveSamplingInputPositions(
        controlPointPositions,
        curveMetadata.closureKind,
    );
    if (curveMetadata.curveKind === 'bezier') {
        return sampleRationalBezierPoints(curveInputPositions, curveMetadata.weights, segments);
    }
    return sampleRationalBSplinePoints(
        curveInputPositions,
        curveMetadata.weights,
        curveMetadata.degree ?? 3,
        segments,
    );
}
